/* istanbul ignore file */
import { MSALHolder } from '@/lib/msal';
import BaseAPI from './baseServiceV2';
import { AxiosInstance } from 'axios';
import {
    ReportDefinitionResponse,
    ReportDefinitionRequest,
    ReportExecutionResult,
    ToggleReportResponse
} from '@/models/reportAI';

class ReportAIServiceV2 extends BaseAPI {
    client!: AxiosInstance;

    static instance: ReportAIServiceV2;

    private constructor() {
        super();
        (async () => {
            const BASE_URL = (
                await MSALHolder.getInstance()
            ).getSpaConfiguration()?.backendReportsAiServiceUrl;
            this.client = this.getClient(BASE_URL!);
        })();

        ReportAIServiceV2.instance = this;
    }

    getDefinitions = async (
        companyId: string
    ): Promise<{ data: ReportDefinitionResponse[] }> => {
        const headers = await this.getAuthHeaders(companyId);
        return this.client.get('/api/ai/definitions', { headers });
    };

    executeReport = async (
        reportId: string,
        companyId: string,
        params: Record<string, string | undefined>
    ): Promise<{ data: ReportExecutionResult }> => {
        const headers = await this.getAuthHeaders(companyId);
        const queryString = new URLSearchParams(
            Object.entries(params).filter(([, v]) => v !== undefined) as [
                string,
                string
            ][]
        ).toString();
        const url = `/api/ai/execute/${reportId}${
            queryString ? `?${queryString}` : ''
        }`;
        return this.client.get(url, { headers });
    };

    createDefinition = async (
        body: ReportDefinitionRequest
    ): Promise<{ data: ReportDefinitionResponse }> => {
        const headers = await this.getAuthHeaders();
        return this.client.post('/api/ai/definitions', body, { headers });
    };

    toggleDefinition = async (
        reportId: string
    ): Promise<{ data: ToggleReportResponse }> => {
        const headers = await this.getAuthHeaders();
        return this.client.patch(
            `/api/ai/definitions/${reportId}/toggle`,
            {},
            { headers }
        );
    };

    deleteDefinition = async (reportId: string): Promise<void> => {
        const headers = await this.getAuthHeaders();
        return this.client.delete(`/api/ai/definitions/${reportId}`, {
            headers
        });
    };

    static getInstance = () => {
        if (this.instance) {
            return this.instance;
        }
        this.instance = new ReportAIServiceV2();
        return this.instance;
    };
}

export default ReportAIServiceV2;
